import React, { useState, useEffect } from 'react';
import './AddTicketModal.css';
import { TICKET_SITES, getSiteDisplayName } from '../utils/siteConstants';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonInput,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonSelect,
  IonSelectOption,
  IonIcon,
  IonChip,
  IonGrid,
  IonRow,
  IonCol,
  IonSpinner,
  IonText,
  IonNote,
  IonBadge,
  IonCheckbox,
  IonToast,
} from '@ionic/react';
import {
  checkmarkCircleOutline,
  closeCircleOutline,
  swapHorizontalOutline,
  locationOutline,
  ticketOutline,
  pricetagOutline,
  cubeOutline,
  businessOutline,
  informationCircleOutline,
  layersOutline,
  gridOutline,
  reorderFourOutline,
  cashOutline,
  cartOutline,
  walletOutline,
  personOutline,
  mailOutline,
  callOutline,
} from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  ticketService,
  stadiumService,
  supplierService,
  fulfillmentService,
} from '../services/api';
import { eventBus, EVENTS } from '../utils/eventBus';
import { Event, Ticket, Supplier, Sale } from '../types';
import SupplierDetailsCard from './SupplierDetailsCard';
import InlineSupplierForm from './InlineSupplierForm';
import {
  validatePrice,
  validateQuantity,
  validateName,
  validateSection,
  validateRow,
  validateSeat,
  validateEmail,
  validatePhone,
  getValidationError,
} from '../utils/validation';
import {
  RESTRICTION_OPTIONS,
  SPLIT_TYPE_OPTIONS,
  calculateSplitGroups,
} from '../utils/ticketConstants';
import {
  CURRENCIES,
  DEFAULT_CURRENCY,
  getCurrencySymbol,
} from '../utils/currencyConstants';

interface AddTicketModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  event: Event | null;
  onSuccess: () => void;
  onBeforeSubmit?: () => Promise<Event | null>;
  ticket?: Ticket | null; // Optional ticket prop for edit mode
  saleItem?: Sale | null; // Optional sale for creating inventory from sale
}

interface FormData {
  eventId: string;
  eventName: string;
  eventDate: Date;
  eventTime: string;
  venue: string;
  category: string;
  block: string;
  row: string;
  seat: string;
  purchasePrice: number;
  wantedPrice: number;
  currency: string;
  quantity: number;
  // Source information
  sourceType: 'site' | 'private' | 'supplier';
  siteId: string; // New field for site selection
  privateName: string; // New field for private source name
  supplierId: string;
  supplier: string;
  newSupplierName: string;
  initialBalance: number;
  // Buyer information
  buyerName: string;
  buyerEmail: string;
  buyerPhone: string;
  status: 'available' | 'sold' | 'reserved';
  restrictions: string[];
  customRestriction: string;
  splitType: string;
  notes: string;
  orderNumber: string;
}

const AddTicketModal: React.FC<AddTicketModalProps> = ({
  isOpen,
  onDismiss,
  event,
  onSuccess,
  onBeforeSubmit,
  ticket,
  saleItem,
}) => {
  const { t } = useTranslation();
  const isEditMode = !!ticket;
  const [stadiumData, setStadiumData] = useState<any>(null);
  const [categories, setCategories] = useState<any[]>([]);
  const [blocks, setBlocks] = useState<any[]>([]);
  const [filteredBlocks, setFilteredBlocks] = useState<any[]>([]);
  const getInitialFormData = (): FormData => {
    // Pre-populate from sale if creating inventory from sale
    if (saleItem && event) {
      const restrictionsList = saleItem.restrictions
        ? saleItem.restrictions
            .split(',')
            .map((r) => r.trim())
            .filter((r) => {
              return (
                RESTRICTION_OPTIONS.some((opt) => opt.id === r) ||
                !RESTRICTION_OPTIONS.some((opt) => opt.label === r)
              );
            })
        : [];

      const formData = {
        eventId: event._id,
        eventName: event.name,
        eventDate: new Date(event.date),
        eventTime: event.time || '',
        venue: event.venue,
        category: saleItem.category
          ? saleItem.category.toLowerCase().replace(/\s+/g, '-')
          : '',
        block: saleItem.block || '',
        row: '', // Clear row field to avoid unnecessary default text
        seat: saleItem.seat || '',
        purchasePrice: 0, // Will be filled by user
        wantedPrice: saleItem.salePrice, // salePrice is already per-ticket
        currency: saleItem.currency || DEFAULT_CURRENCY,
        quantity: saleItem.quantity || 1, // Default to sale quantity
        sourceType: (saleItem.saleType === 'supplier' ? 'supplier' : 'site') as
          | 'supplier'
          | 'site',
        siteId: 'official_club_site',
        privateName:
          saleItem.saleType === 'private' ? saleItem.buyerName || '' : '',
        supplierId:
          typeof saleItem.supplierId === 'string' ? saleItem.supplierId : '',
        supplier: '',
        newSupplierName: '',
        initialBalance: 0,
        // Buyer information from sale
        buyerName: saleItem.buyerName || '',
        buyerEmail: saleItem.buyerEmail || '',
        buyerPhone: saleItem.buyerPhone || '',
        status: 'available' as 'available' | 'sold' | 'reserved',
        restrictions: restrictionsList,
        customRestriction: '',
        splitType: saleItem.split || 'all-together',
        notes: '',
        orderNumber: '',
      };

      return formData;
    }

    if (isEditMode && ticket && event) {
      const restrictionsList = ticket.restrictions
        ? ticket.restrictions
            .split(',')
            .map((r) => r.trim())
            .filter((r) => {
              // Check if it's a predefined restriction
              return (
                RESTRICTION_OPTIONS.some((opt) => opt.id === r) ||
                !RESTRICTION_OPTIONS.some((opt) => opt.label === r)
              );
            })
        : [];

      return {
        eventId: event._id,
        eventName: event.name,
        eventDate: new Date(event.date),
        eventTime: event.time || '',
        venue: event.venue,
        category: ticket.category || '',
        block: ticket.block || '',
        row: ticket.row || '',
        seat: ticket.seat || '',
        purchasePrice: ticket.purchasePrice || 0,
        wantedPrice: ticket.wantedPrice || 0,
        currency: ticket.currency || DEFAULT_CURRENCY,
        quantity: ticket.quantity || 1,
        sourceType: ticket.sourceType || 'site',
        siteId: (() => {
          // Check if supplier matches any site name
          if (ticket.sourceType === 'site' && ticket.supplier) {
            const matchingSite = TICKET_SITES.find(
              (site) =>
                site.displayName.toLowerCase() ===
                ticket.supplier?.toLowerCase()
            );
            return matchingSite?.id || 'official_club_site';
          }
          return 'official_club_site';
        })(),
        privateName:
          ticket.sourceType === 'private' && ticket.supplier
            ? ticket.supplier
            : '',
        supplierId:
          typeof ticket.supplierId === 'string' ? ticket.supplierId : '',
        supplier: ticket.supplier || '',
        newSupplierName: '',
        initialBalance: 0,
        // Buyer information
        buyerName: '',
        buyerEmail: '',
        buyerPhone: '',
        status: ticket.status as 'available' | 'sold' | 'reserved',
        restrictions: restrictionsList,
        customRestriction: '',
        splitType: ticket.split || 'all-together',
        notes: ticket.notes || '',
        orderNumber: ticket.orderNumber || '',
      };
    }

    return {
      eventId: event?._id || '',
      eventName: event?.name || '',
      eventDate: event ? new Date(event.date) : new Date(),
      eventTime: event?.time || '',
      venue: event?.venue || '',
      category: '',
      block: '',
      row: '',
      seat: '',
      purchasePrice: 0,
      wantedPrice: 0,
      currency: DEFAULT_CURRENCY,
      quantity: 1,
      sourceType: 'supplier',
      siteId: 'official_club_site',
      privateName: '',
      supplierId: '',
      supplier: '',
      newSupplierName: '',
      initialBalance: 0,
      // Buyer information
      buyerName: '',
      buyerEmail: '',
      buyerPhone: '',
      status: 'available',
      restrictions: [],
      customRestriction: '',
      splitType: 'all-together',
      notes: '',
      orderNumber: '',
    };
  };

  const [formData, setFormData] = useState<FormData>(getInitialFormData());
  const [loading, setLoading] = useState(false);
  const [loadingStadium, setLoadingStadium] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [categoriesMapped, setCategoriesMapped] = useState(false);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [showNewSupplierInput, setShowNewSupplierInput] = useState(false);
  const [showInlineSupplierForm, setShowInlineSupplierForm] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(
    null
  );
  const [loadingSupplier, setLoadingSupplier] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Update form data when modal opens or props change
  useEffect(() => {
    if (event && isOpen) {
      const newFormData = getInitialFormData();
      console.log('Modal opened with form data:', newFormData);
      console.log('Sale item category:', saleItem?.category);
      setFormData(newFormData);
      setCategoriesMapped(false); // Reset mapping flag
      // Reset stadium data when opening modal
      setCategories([]);
      setBlocks([]);
      setFilteredBlocks([]);
      setStadiumData(null);
    }
  }, [isOpen, event?._id, saleItem]);

  // Load stadium data when venue is available
  useEffect(() => {
    if (event && isOpen) {
      loadStadiumData(event.venue);
      loadSuppliers();
    }
  }, [event?.venue, isOpen]);

  // Simplified category/block mapping - removed as it's overcomplicating things
  // The form data already has the correct category from the sale

  const loadStadiumData = async (venue: string) => {
    setLoadingStadium(true);

    // Always reset to defaults first
    setStadiumData(null);
    setBlocks([]);
    setFilteredBlocks([]);

    try {
      const data = await stadiumService.getStadiumData(venue);

      if (data && data.categories && data.categories.length > 0) {
        // Stadium exists with categories
        setStadiumData(data);

        // Always include the sale's category if it exists
        if (saleItem?.category) {
          // Check if the sale's category exists in the stadium categories
          const matchingCategory = data.categories.find(
            (c: any) =>
              c.name.toLowerCase() === saleItem.category?.toLowerCase()
          );

          if (!matchingCategory) {
            // Add the sale's category at the beginning of the list with a normalized ID
            const normalizedId = saleItem.category
              .toLowerCase()
              .replace(/\s+/g, '-');
            setCategories([
              {
                id: normalizedId,
                name: saleItem.category,
              },
              ...data.categories,
            ]);
            // Update form data to use the normalized ID
            setFormData((prev) => ({ ...prev, category: normalizedId }));
          } else {
            setCategories(data.categories);
            // Update form data to use the matching category's ID
            setFormData((prev) => ({ ...prev, category: matchingCategory.id }));
          }
        } else {
          setCategories(data.categories);
        }

        setBlocks(data.blocks || []);
        setFilteredBlocks(data.blocks || []);
      } else {
        // Stadium exists but has no categories OR no data returned
        const defaultCategories = [
          { id: 'shortside', name: 'Shortside' },
          { id: 'longside', name: 'Longside' },
          { id: 'away', name: 'Away' },
        ];

        // If we have a sale category, always include it
        if (saleItem?.category) {
          const matchingCategory = defaultCategories.find(
            (c) => c.name.toLowerCase() === saleItem.category?.toLowerCase()
          );

          if (!matchingCategory) {
            const normalizedId = saleItem.category
              .toLowerCase()
              .replace(/\s+/g, '-');
            setCategories([
              {
                id: normalizedId,
                name: saleItem.category,
              },
              ...defaultCategories,
            ]);
            // Update form data to use the normalized ID
            setFormData((prev) => ({ ...prev, category: normalizedId }));
          } else {
            setCategories(defaultCategories);
            // Update form data to use the matching category's ID
            setFormData((prev) => ({ ...prev, category: matchingCategory.id }));
          }
        } else {
          setCategories(defaultCategories);
        }
      }
    } catch (error: any) {
      // Set default categories when stadium doesn't exist
      const defaultCategories = [
        { id: 'shortside', name: 'Shortside' },
        { id: 'longside', name: 'Longside' },
      ];

      // If we have a sale category, include it
      if (saleItem?.category) {
        const matchingCategory = defaultCategories.find(
          (c) => c.name.toLowerCase() === saleItem.category?.toLowerCase()
        );

        if (!matchingCategory) {
          const normalizedId = saleItem.category
            .toLowerCase()
            .replace(/\s+/g, '-');
          setCategories([
            {
              id: normalizedId,
              name: saleItem.category,
            },
            ...defaultCategories,
          ]);
          // Update form data to use the normalized ID
          setFormData((prev) => ({ ...prev, category: normalizedId }));
        } else {
          setCategories(defaultCategories);
          // Update form data to use the matching category's ID
          setFormData((prev) => ({ ...prev, category: matchingCategory.id }));
        }
      } else {
        setCategories(defaultCategories);
      }
    } finally {
      setLoadingStadium(false);
    }
  };

  const loadSuppliers = async () => {
    try {
      const data = await supplierService.getAllWithBalance();
      setSuppliers(data || []);
    } catch (error) {
      console.error('Failed to load suppliers:', error);
      setSuppliers([]);
    }
  };

  // Filter blocks based on selected category
  useEffect(() => {
    if (formData.category && blocks.length > 0) {
      const filtered = blocks.filter((block) =>
        block.categories.includes(formData.category)
      );
      setFilteredBlocks(filtered);
    } else {
      setFilteredBlocks(blocks);
    }
  }, [formData.category, blocks]);

  // Set default supplier when suppliers are loaded and sourceType is supplier
  useEffect(() => {
    if (
      suppliers.length > 0 &&
      formData.sourceType === 'supplier' &&
      !formData.supplierId &&
      !isEditMode
    ) {
      // If we're creating inventory from a sale and it has a supplier ID, use it
      if (saleItem?.supplierId) {
        const saleSupplier = suppliers.find(
          (s) => (s._id || s.id) === saleItem.supplierId
        );
        if (saleSupplier) {
          const supplierId = saleSupplier._id || saleSupplier.id || '';
          setFormData((prev) => ({ ...prev, supplierId }));
          setSelectedSupplier(saleSupplier);
          return;
        }
      }

      // Don't set any default supplier - user must choose
      // The supplier dropdown will show "Select Supplier" as placeholder
    }
  }, [
    suppliers,
    formData.sourceType,
    formData.supplierId,
    isEditMode,
    saleItem?.supplierId,
  ]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Validate price
    if (!validatePrice(formData.purchasePrice)) {
      newErrors.purchasePrice =
        getValidationError('purchasePrice', formData.purchasePrice) || '';
    }

    // Validate quantity
    if (!validateQuantity(formData.quantity)) {
      newErrors.quantity =
        getValidationError('quantity', formData.quantity) || '';
    }

    // Additional validation when coming from a sale
    if (
      saleItem &&
      saleItem.quantity &&
      formData.quantity > saleItem.quantity
    ) {
      newErrors.quantity = `Cannot add more than ${saleItem.quantity} tickets (sale quantity)`;
    }

    // Validate supplier if provided
    if (formData.supplier && !validateName(formData.supplier)) {
      newErrors.supplier =
        getValidationError('supplier', formData.supplier) || '';
    }

    // Validate location fields if provided
    if (formData.row && !validateRow(formData.row)) {
      newErrors.row = getValidationError('row', formData.row) || '';
    }

    if (formData.seat && !validateSeat(formData.seat)) {
      newErrors.seat = getValidationError('seat', formData.seat) || '';
    }

    // Validate category (required)
    if (!formData.category || formData.category.trim() === '') {
      newErrors.category = 'Category is required';
    }

    // Validate split type (required)
    if (!formData.splitType || formData.splitType.trim() === '') {
      newErrors.splitType = 'Split type is required';
    }

    // Validate site selection for site source type
    if (formData.sourceType === 'site' && !formData.siteId) {
      newErrors.siteId = 'Please select a marketplace site';
    }

    // Validate buyer name for private source type (required)
    if (
      formData.sourceType === 'private' &&
      (!formData.buyerName || formData.buyerName.trim() === '')
    ) {
      newErrors.buyerName = 'Buyer name is required for private purchases';
    }

    // Validate supplier selection for supplier source type
    if (formData.sourceType === 'supplier' && !formData.supplierId) {
      newErrors.supplierId = 'Supplier selection is required';
    }

    // Validate buyer name format if provided
    if (formData.buyerName && !validateName(formData.buyerName)) {
      newErrors.buyerName =
        getValidationError('buyerName', formData.buyerName) || '';
    }

    if (formData.buyerEmail && !validateEmail(formData.buyerEmail)) {
      newErrors.buyerEmail =
        getValidationError('email', formData.buyerEmail) || '';
    }

    if (formData.buyerPhone && !validatePhone(formData.buyerPhone)) {
      newErrors.buyerPhone =
        getValidationError('phone', formData.buyerPhone) || '';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = () => {
    return (
      formData.purchasePrice > 0 &&
      formData.quantity > 0 &&
      formData.category &&
      formData.category.trim() !== '' &&
      formData.splitType &&
      formData.splitType.trim() !== '' &&
      (formData.sourceType !== 'site' || formData.siteId) &&
      (formData.sourceType !== 'private' ||
        (formData.buyerName && formData.buyerName.trim() !== '')) &&
      (formData.sourceType !== 'supplier' || formData.supplierId)
    );
  };

  const handleSubmit = async () => {
    if (!event || !isFormValid() || !validateForm()) return;

    setLoading(true);
    try {
      // If the event is temporary, create the real event first
      let actualEvent = event;
      if (event._id.startsWith('temp-') && onBeforeSubmit && !isEditMode) {
        const createdEvent = await onBeforeSubmit();
        if (!createdEvent) {
          // Event creation failed
          setLoading(false);
          return;
        }
        actualEvent = createdEvent;
      }

      const ticketData: any = {
        purchasePrice: formData.purchasePrice,
        wantedPrice: formData.wantedPrice || undefined,
        currency: formData.currency,
        quantity: formData.quantity,
        sourceType: formData.sourceType,
        status: formData.status,
        notes: formData.notes || undefined,
        orderNumber: formData.orderNumber || undefined,
      };

      // Handle source logic
      if (formData.sourceType === 'site') {
        // For site purchases, store the site name in the supplier field
        if (formData.siteId) {
          const siteName = getSiteDisplayName(formData.siteId);
          ticketData.supplier = siteName;
        }
      } else if (formData.sourceType === 'supplier') {
        if (formData.supplierId === 'new' && formData.newSupplierName.trim()) {
          // Create new supplier using supplier name
          ticketData.supplier = formData.newSupplierName.trim();
          if (formData.initialBalance !== 0) {
            ticketData.initialBalance = formData.initialBalance;
          }
        } else if (formData.supplierId && formData.supplierId !== 'new') {
          ticketData.supplierId = formData.supplierId;
        } else if (formData.supplier) {
          ticketData.supplier = formData.supplier;
        }
      } else {
        // For private purchases, use privateName if provided
        if (formData.privateName) {
          ticketData.supplier = formData.privateName;
        }
      }

      // Only include eventId for create, not update
      if (!isEditMode) {
        ticketData.eventId = actualEvent._id;
      }

      // Include category and block information
      if (formData.category) {
        // Find the category name from the ID
        const selectedCategory = categories.find(
          (c) => c.id === formData.category
        );
        if (selectedCategory) {
          ticketData.category = selectedCategory.name; // Use name instead of ID
        } else {
          // If no category found (manual input), use the value as is
          ticketData.category = formData.category;
        }
      }
      // Always include block field to handle clearing
      if (formData.block && formData.block !== '') {
        // Find the block name from the ID
        const selectedBlock = blocks.find((b) => b.id === formData.block);
        if (selectedBlock) {
          ticketData.block = selectedBlock.name; // Use name instead of ID
          ticketData.section = selectedBlock.name; // For backward compatibility
        } else {
          // If no block found (manual input), use the value as is
          ticketData.block = formData.block;
          ticketData.section = formData.block;
        }
      } else {
        // Explicitly set block to empty string to clear it
        ticketData.block = '';

        if (formData.category) {
          // If only category is selected, use category name as section
          const selectedCategory = categories.find(
            (c) => c.id === formData.category
          );
          if (selectedCategory) {
            ticketData.section = selectedCategory.name;
          } else {
            ticketData.section = formData.category;
          }
        }
      }

      // Include row and seat if provided
      if (formData.row) {
        ticketData.row = formData.row;
      }
      if (formData.seat) {
        ticketData.seat = formData.seat;
      }

      // Include restrictions and split type
      if (formData.restrictions.length > 0) {
        ticketData.restrictions = formData.restrictions.join(', ');
      }
      // Always include split type since it's mandatory
      ticketData.split = formData.splitType;

      if (isEditMode && ticket) {
        // Update existing ticket
        await ticketService.update(ticket.id || ticket._id || '', ticketData);
      } else {
        // Create new ticket
        const result = await ticketService.create(ticketData);

        // If creating from a sale, link the ticket to the sale
        if (saleItem && result) {
          const saleId = saleItem.id || saleItem._id;
          const ticketId = (result as any).id || (result as any)._id;

          if (saleId && ticketId) {
            // Call the fulfillment service to link the sale with the new ticket
            try {
              // Use the actual quantity being added, not the full sale quantity
              const quantityToLink = formData.quantity;
              await fulfillmentService.linkSaleToInventory(
                saleId,
                ticketId,
                quantityToLink
              );
              console.log(
                `Successfully linked ${quantityToLink} tickets from inventory to sale`
              );

              // Emit event to update the sales display
              eventBus.emit(EVENTS.SALE_UPDATED, {
                saleId,
                ticketIds: [ticketId],
              });
            } catch (error) {
              console.error('Error linking inventory to sale:', error);
              // Don't throw - ticket was created successfully
            }
          }
        }

        // DISABLED: Auto-fulfillment notifications per user request
        // Check if any sales were auto-fulfilled
        // if (result && (result as any).fulfillmentResult) {
        //   const { salesFulfilled, ticketsUsed } = (result as any).fulfillmentResult;
        //   eventBus.emit(EVENTS.SALES_FULFILLED, {
        //     count: salesFulfilled,
        //     ticketsUsed: ticketsUsed
        //   });
        // }
      }

      resetForm();
      onSuccess();
      onDismiss();
    } catch (error) {
      console.error(
        isEditMode ? 'Error updating ticket:' : 'Error creating ticket:',
        error
      );
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(getInitialFormData());
    setErrors({});
    setSelectedSupplier(null);
    setShowInlineSupplierForm(false);
    setShowNewSupplierInput(false);
  };

  const handleClose = () => {
    resetForm();
    onDismiss();
  };

  const calculateTotalCost = () => {
    return formData.purchasePrice * formData.quantity;
  };

  const getCategoryBadgeColor = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'vip':
        return 'warning';
      case 'premium':
        return 'tertiary';
      default:
        return 'primary';
    }
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={handleClose}
      className="add-ticket-modal"
    >
      <IonHeader>
        <IonToolbar className="border-b">
          <IonTitle className="text-xl font-bold">
            <IonIcon icon={ticketOutline} className="mr-2 align-middle" />
            {isEditMode ? t('inventory.editTicket') : t('inventory.addTicket')}
          </IonTitle>
          <IonButtons slot="start">
            <IonButton onClick={handleClose} fill="clear" color="medium">
              <IonIcon icon={closeCircleOutline} slot="icon-only" />
            </IonButton>
          </IonButtons>
          <IonButtons slot="end">
            <IonButton
              onClick={handleSubmit}
              disabled={loading || !event || !isFormValid()}
              fill="solid"
              color="primary"
              className="mr-2"
            >
              {loading ? (
                <IonSpinner name="dots" />
              ) : (
                <>
                  <IonIcon icon={checkmarkCircleOutline} slot="start" />
                  {isEditMode ? t('common.save') : t('common.add')}
                </>
              )}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="bg-gray-50 dark:bg-gray-950">
        {event && (
          <div className="max-w-4xl mx-auto p-4">
            {/* Event Info Card */}
            <IonCard className="mb-6 shadow-lg border border-primary-200 dark:border-primary-800">
              <IonCardHeader className="bg-primary-50 dark:bg-primary-900/30 pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <IonCardTitle className="text-xl font-bold text-primary-900 dark:text-primary-100">
                      {event.name}
                    </IonCardTitle>
                    <IonText className="text-sm text-gray-600 dark:text-gray-400 mt-1 block">
                      <IonIcon
                        icon={locationOutline}
                        className="mr-1 align-middle"
                      />
                      {event.venue}
                    </IonText>
                  </div>
                  <IonChip color="primary" className="mt-1">
                    {format(new Date(event.date), 'MMM dd')}
                    {event.time && ` • ${event.time}`}
                  </IonChip>
                </div>
              </IonCardHeader>
            </IonCard>

            {/* Source Type Selection */}
            <IonCard className="mb-4 shadow-md">
              <IonCardHeader className="pb-2 bg-gray-50 dark:bg-gray-900/50">
                <IonCardTitle className="text-lg flex items-center">
                  <IonIcon icon={swapHorizontalOutline} className="mr-2" />
                  Source of Inventory
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <IonList className="space-y-3">
                  <IonItem
                    lines="none"
                    className="bg-gray-50 dark:bg-gray-900 rounded-lg"
                  >
                    <IonLabel
                      position="stacked"
                      className="text-sm font-medium mb-1"
                    >
                      Source Type
                    </IonLabel>
                    <IonSelect
                      value={formData.sourceType}
                      onIonChange={(e) => {
                        setFormData({
                          ...formData,
                          sourceType: e.detail.value,
                          siteId: '',
                          privateName: '',
                          supplierId: '',
                          supplier: '',
                          newSupplierName: '',
                        });
                        setShowNewSupplierInput(false);
                      }}
                    >
                      <IonSelectOption value="site">
                        Site Purchase
                      </IonSelectOption>
                      <IonSelectOption value="private">
                        Private Purchase
                      </IonSelectOption>
                      <IonSelectOption value="supplier">
                        Supplier
                      </IonSelectOption>
                    </IonSelect>
                  </IonItem>

                  {formData.sourceType === 'site' && (
                    <IonItem
                      lines="none"
                      className={`bg-gray-50 dark:bg-gray-900 rounded-lg ${
                        errors.siteId ? 'ion-invalid' : ''
                      }`}
                    >
                      <IonLabel
                        position="stacked"
                        className="text-sm font-medium mb-1"
                      >
                        Select Site <span className="text-red-500">*</span>
                      </IonLabel>
                      <IonSelect
                        value={formData.siteId}
                        onIonChange={(e) => {
                          setFormData({
                            ...formData,
                            siteId: e.detail.value,
                          });
                          // Clear error when user selects
                          if (errors.siteId) {
                            setErrors({ ...errors, siteId: '' });
                          }
                        }}
                        placeholder="Choose marketplace"
                      >
                        {TICKET_SITES.map((site) => (
                          <IonSelectOption key={site.id} value={site.id}>
                            {site.displayName}
                          </IonSelectOption>
                        ))}
                      </IonSelect>
                      {errors.siteId && (
                        <IonNote
                          slot="error"
                          className="text-red-500 text-xs mt-1"
                        >
                          {errors.siteId}
                        </IonNote>
                      )}
                    </IonItem>
                  )}

                  {formData.sourceType === 'supplier' && (
                    <>
                      <IonItem
                        lines="none"
                        className={`bg-gray-50 dark:bg-gray-900 rounded-lg ${
                          errors.supplierId ? 'ion-invalid' : ''
                        }`}
                      >
                        <IonLabel
                          position="stacked"
                          className="text-sm font-medium mb-1"
                        >
                          Supplier <span className="text-red-500">*</span>
                        </IonLabel>
                        <IonSelect
                          value={formData.supplierId}
                          onIonChange={(e) => {
                            const value = e.detail.value;
                            if (value === 'new') {
                              setShowInlineSupplierForm(true);
                              setFormData({
                                ...formData,
                                supplierId: '',
                              });
                              setSelectedSupplier(null);
                            } else {
                              setFormData({
                                ...formData,
                                supplierId: value,
                              });
                              // Find and set selected supplier details
                              const supplier = suppliers.find(
                                (s) => (s._id || s.id) === value
                              );
                              setSelectedSupplier(supplier || null);
                              setShowInlineSupplierForm(false);
                              // Clear error when user selects
                              if (errors.supplierId) {
                                setErrors({ ...errors, supplierId: '' });
                              }
                            }
                            setShowNewSupplierInput(false);
                          }}
                          placeholder="Choose a supplier"
                        >
                          <IonSelectOption value="">
                            Select Supplier
                          </IonSelectOption>
                          {suppliers.map((supplier) => (
                            <IonSelectOption
                              key={supplier._id || supplier.id}
                              value={supplier._id || supplier.id}
                            >
                              {supplier.name}
                            </IonSelectOption>
                          ))}
                          <IonSelectOption value="new">
                            + Add New Supplier
                          </IonSelectOption>
                        </IonSelect>
                        {errors.supplierId && (
                          <IonNote
                            slot="error"
                            className="text-red-500 text-xs mt-1"
                          >
                            {errors.supplierId}
                          </IonNote>
                        )}
                      </IonItem>

                      {showInlineSupplierForm && (
                        <InlineSupplierForm
                          onCancel={() => {
                            setShowInlineSupplierForm(false);
                            setFormData({ ...formData, supplierId: '' });
                          }}
                          onSave={async (supplierData) => {
                            try {
                              setLoadingSupplier(true);
                              // Create the supplier
                              const newSupplier = await supplierService.create(
                                supplierData
                              );

                              // Add to suppliers list
                              setSuppliers([...suppliers, newSupplier]);

                              // Select the new supplier
                              setFormData({
                                ...formData,
                                supplierId: newSupplier._id || newSupplier.id,
                              });
                              setSelectedSupplier(newSupplier);

                              // Show success message
                              setToastMessage('Supplier created successfully');
                              setShowToast(true);

                              // Hide the form
                              setShowInlineSupplierForm(false);
                            } catch (error: any) {
                              console.error('Error creating supplier:', error);
                              const errorMessage =
                                error.response?.data?.message ||
                                error.message ||
                                'Failed to create supplier. Please try again.';
                              setToastMessage(errorMessage);
                              setShowToast(true);
                            } finally {
                              setLoadingSupplier(false);
                            }
                          }}
                        />
                      )}

                      {selectedSupplier && !showInlineSupplierForm && (
                        <div className="mt-4">
                          <SupplierDetailsCard
                            supplier={selectedSupplier}
                            loading={loadingSupplier}
                          />
                        </div>
                      )}
                    </>
                  )}
                </IonList>
              </IonCardContent>
            </IonCard>

            <IonGrid className="p-0">
              <IonRow>
                {/* Left Column - Location Details */}
                <IonCol size="12" sizeMd="6" className="pr-0 md:pr-2">
                  {/* Buyer Information - Only for Private Sales */}
                  {formData.sourceType === 'private' && (
                    <IonCard className="mb-4 shadow-md">
                      <IonCardHeader className="pb-2 bg-gray-50 dark:bg-gray-900/50">
                        <IonCardTitle className="text-lg flex items-center">
                          <IonIcon icon={personOutline} className="mr-2" />
                          Buyer Information
                        </IonCardTitle>
                      </IonCardHeader>
                      <IonCardContent>
                        <IonList className="space-y-3">
                          <IonItem
                            lines="none"
                            className={`bg-gray-50 dark:bg-gray-900 rounded-lg ${
                              errors.buyerName ? 'ion-invalid' : ''
                            }`}
                          >
                            <IonLabel
                              position="stacked"
                              className="text-sm font-medium mb-1"
                            >
                              Buyer Name <span className="text-red-500">*</span>
                            </IonLabel>
                            <IonInput
                              value={formData.buyerName}
                              onIonChange={(e) => {
                                setFormData({
                                  ...formData,
                                  buyerName: e.detail.value!,
                                });
                                // Clear error when user starts typing
                                if (errors.buyerName) {
                                  setErrors({ ...errors, buyerName: '' });
                                }
                              }}
                              placeholder="John Doe"
                              className="text-base"
                            />
                            {errors.buyerName && (
                              <IonNote
                                slot="error"
                                className="text-red-500 text-xs mt-1"
                              >
                                {errors.buyerName}
                              </IonNote>
                            )}
                          </IonItem>
                          <IonItem
                            lines="none"
                            className={`bg-gray-50 dark:bg-gray-900 rounded-lg ${
                              errors.buyerEmail ? 'ion-invalid' : ''
                            }`}
                          >
                            <IonIcon
                              icon={mailOutline}
                              slot="start"
                              color="medium"
                            />
                            <IonLabel
                              position="stacked"
                              className="text-sm font-medium mb-1"
                            >
                              Email
                            </IonLabel>
                            <IonInput
                              type="email"
                              value={formData.buyerEmail}
                              onIonChange={(e) => {
                                setFormData({
                                  ...formData,
                                  buyerEmail: e.detail.value!,
                                });
                                // Clear error when user starts typing
                                if (errors.buyerEmail) {
                                  setErrors({ ...errors, buyerEmail: '' });
                                }
                              }}
                              placeholder="john.doe@example.com"
                              className="text-base"
                            />
                            {errors.buyerEmail && (
                              <IonNote
                                slot="error"
                                className="text-red-500 text-xs mt-1"
                              >
                                {errors.buyerEmail}
                              </IonNote>
                            )}
                          </IonItem>
                          <IonItem
                            lines="none"
                            className={`bg-gray-50 dark:bg-gray-900 rounded-lg ${
                              errors.buyerPhone ? 'ion-invalid' : ''
                            }`}
                          >
                            <IonIcon
                              icon={callOutline}
                              slot="start"
                              color="medium"
                            />
                            <IonLabel
                              position="stacked"
                              className="text-sm font-medium mb-1"
                            >
                              Phone
                            </IonLabel>
                            <IonInput
                              type="tel"
                              value={formData.buyerPhone}
                              onIonChange={(e) => {
                                setFormData({
                                  ...formData,
                                  buyerPhone: e.detail.value!,
                                });
                                // Clear error when user starts typing
                                if (errors.buyerPhone) {
                                  setErrors({ ...errors, buyerPhone: '' });
                                }
                              }}
                              placeholder="+44 7000 000000"
                              className="text-base"
                            />
                            {errors.buyerPhone && (
                              <IonNote
                                slot="error"
                                className="text-red-500 text-xs mt-1"
                              >
                                {errors.buyerPhone}
                              </IonNote>
                            )}
                          </IonItem>
                        </IonList>
                      </IonCardContent>
                    </IonCard>
                  )}

                  {/* Location Information */}
                  <IonCard className="mb-4 shadow-md">
                    <IonCardHeader className="pb-2 bg-gray-50 dark:bg-gray-900/50">
                      <IonCardTitle className="text-lg flex items-center justify-between">
                        <span className="flex items-center">
                          <IonIcon icon={locationOutline} className="mr-2" />
                          {t('inventory.location')}
                        </span>
                        {loadingStadium && (
                          <IonSpinner name="dots" className="h-5 w-5" />
                        )}
                      </IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent>
                      <IonList className="space-y-3">
                        {/* Category Selection */}
                        <IonItem
                          lines="none"
                          className={`bg-gray-50 dark:bg-gray-900 rounded-lg ${
                            errors.category ? 'ion-invalid' : ''
                          }`}
                        >
                          <IonIcon
                            icon={layersOutline}
                            slot="start"
                            color="medium"
                          />
                          <IonLabel
                            position="stacked"
                            className="text-sm font-medium mb-1"
                          >
                            Category <span className="text-red-500">*</span>
                            {!loadingStadium && categories.length > 0 && (
                              <IonBadge color="primary" className="ml-2">
                                {categories.length} available
                              </IonBadge>
                            )}
                          </IonLabel>
                          {loadingStadium ? (
                            <div className="flex items-center justify-center py-2">
                              <IonSpinner name="dots" className="h-6 w-6" />
                            </div>
                          ) : categories.length > 0 ? (
                            <IonSelect
                              value={formData.category}
                              onIonChange={(e) => {
                                setFormData({
                                  ...formData,
                                  category: e.detail.value,
                                  block: '',
                                });
                                // Clear error when user selects
                                if (errors.category) {
                                  setErrors({ ...errors, category: '' });
                                }
                              }}
                              placeholder="Select category"
                              interface="popover"
                              className="text-base"
                            >
                              {categories.map((category) => (
                                <IonSelectOption
                                  key={category.id}
                                  value={category.id}
                                >
                                  {category.name}
                                  {category.type && (
                                    <IonBadge
                                      color={getCategoryBadgeColor(
                                        category.type
                                      )}
                                      className="ml-2"
                                    ></IonBadge>
                                  )}
                                </IonSelectOption>
                              ))}
                            </IonSelect>
                          ) : (
                            <IonInput
                              value={formData.category}
                              onIonChange={(e) => {
                                setFormData({
                                  ...formData,
                                  category: e.detail.value!,
                                  block: '',
                                });
                                // Clear error when user types
                                if (errors.category) {
                                  setErrors({ ...errors, category: '' });
                                }
                              }}
                              placeholder="e.g., Upper Bowl, VIP"
                              className="text-base"
                            />
                          )}
                          {errors.category && (
                            <IonNote
                              slot="error"
                              className="text-red-500 text-xs mt-1"
                            >
                              {errors.category}
                            </IonNote>
                          )}
                        </IonItem>

                        {/* Block Selection */}
                        <IonItem
                          lines="none"
                          className="bg-gray-50 dark:bg-gray-900 rounded-lg"
                        >
                          <IonIcon
                            icon={gridOutline}
                            slot="start"
                            color="medium"
                          />
                          <IonLabel
                            position="stacked"
                            className="text-sm font-medium mb-1"
                          >
                            Block
                            {!loadingStadium &&
                              formData.category &&
                              filteredBlocks.length > 0 && (
                                <IonBadge color="secondary" className="ml-2">
                                  {filteredBlocks.length} in category
                                </IonBadge>
                              )}
                          </IonLabel>
                          {loadingStadium ? (
                            <div className="flex items-center justify-center py-2">
                              <IonSpinner name="dots" className="h-6 w-6" />
                            </div>
                          ) : blocks.length > 0 ? (
                            <IonSelect
                              key={`block-select-${formData.category}`}
                              value={formData.block}
                              onIonChange={(e) => {
                                setFormData({
                                  ...formData,
                                  block: e.detail.value,
                                });
                              }}
                              placeholder="Select block (optional)"
                              disabled={
                                !formData.category &&
                                filteredBlocks.length !== blocks.length
                              }
                              interface="popover"
                              className="text-base"
                            >
                              <IonSelectOption value="">
                                No Block Selected
                              </IonSelectOption>
                              {filteredBlocks.map((block) => (
                                <IonSelectOption
                                  key={block.id}
                                  value={block.id}
                                >
                                  {block.name}
                                </IonSelectOption>
                              ))}
                            </IonSelect>
                          ) : (
                            <IonInput
                              value={formData.block}
                              onIonChange={(e) =>
                                setFormData({
                                  ...formData,
                                  block: e.detail.value!,
                                })
                              }
                              placeholder="e.g., Block A1, Section 123"
                              className="text-base"
                            />
                          )}
                        </IonItem>

                        <IonGrid className="p-0">
                          <IonRow>
                            <IonCol size="6" className="pr-1">
                              <IonItem
                                lines="none"
                                className={`bg-gray-50 dark:bg-gray-900 rounded-lg ${
                                  errors.row ? 'ion-invalid' : ''
                                }`}
                              >
                                <IonLabel
                                  position="stacked"
                                  className="text-sm font-medium mb-1"
                                >
                                  {t('inventory.row')}
                                </IonLabel>
                                <IonInput
                                  value={formData.row}
                                  onIonChange={(e) => {
                                    setFormData({
                                      ...formData,
                                      row: e.detail.value!,
                                    });
                                    // Clear error when user starts typing
                                    if (errors.row) {
                                      setErrors({ ...errors, row: '' });
                                    }
                                  }}
                                  placeholder="e.g., A, 12"
                                  className="text-base"
                                />
                                {errors.row && (
                                  <IonNote
                                    slot="error"
                                    className="text-red-500 text-xs mt-1"
                                  >
                                    {errors.row}
                                  </IonNote>
                                )}
                              </IonItem>
                            </IonCol>
                            <IonCol size="6" className="pl-1">
                              <IonItem
                                lines="none"
                                className={`bg-gray-50 dark:bg-gray-900 rounded-lg ${
                                  errors.seat ? 'ion-invalid' : ''
                                }`}
                              >
                                <IonLabel
                                  position="stacked"
                                  className="text-sm font-medium mb-1"
                                >
                                  Seat
                                </IonLabel>
                                <IonInput
                                  value={formData.seat}
                                  onIonChange={(e) => {
                                    setFormData({
                                      ...formData,
                                      seat: e.detail.value!,
                                    });
                                    // Clear error when user starts typing
                                    if (errors.seat) {
                                      setErrors({ ...errors, seat: '' });
                                    }
                                  }}
                                  placeholder="e.g., 14"
                                  className="text-base"
                                />
                                {errors.seat && (
                                  <IonNote
                                    slot="error"
                                    className="text-red-500 text-xs mt-1"
                                  >
                                    {errors.seat}
                                  </IonNote>
                                )}
                              </IonItem>
                            </IonCol>
                          </IonRow>
                        </IonGrid>
                      </IonList>
                    </IonCardContent>
                  </IonCard>

                  {/* Ticket Organization */}
                  <IonCard className="shadow-md">
                    <IonCardHeader className="pb-2 bg-gray-50 dark:bg-gray-900/50">
                      <IonCardTitle className="text-lg flex items-center">
                        <IonIcon icon={layersOutline} className="mr-2" />
                        Ticket Organization
                      </IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent>
                      <IonList className="space-y-3">
                        {/* Split Type Selection */}
                        <IonItem
                          lines="none"
                          className={`bg-gray-50 dark:bg-gray-900 rounded-lg ${
                            errors.splitType ? 'ion-invalid' : ''
                          }`}
                        >
                          <IonIcon
                            icon={swapHorizontalOutline}
                            slot="start"
                            color="medium"
                          />
                          <IonLabel
                            position="stacked"
                            className="text-sm font-medium mb-1"
                          >
                            Split Type <span className="text-red-500">*</span>
                          </IonLabel>
                          <IonSelect
                            value={formData.splitType}
                            onIonChange={(e) => {
                              setFormData({
                                ...formData,
                                splitType: e.detail.value!,
                              });
                              // Clear error when user selects
                              if (errors.splitType) {
                                setErrors({ ...errors, splitType: '' });
                              }
                            }}
                            interface="popover"
                            className="text-base"
                          >
                            {SPLIT_TYPE_OPTIONS.map((option) => (
                              <IonSelectOption
                                key={option.id}
                                value={option.id}
                              >
                                {option.label}
                              </IonSelectOption>
                            ))}
                          </IonSelect>
                          {errors.splitType && (
                            <IonNote
                              slot="error"
                              className="text-red-500 text-xs mt-1"
                            >
                              {errors.splitType}
                            </IonNote>
                          )}
                          <IonNote slot="helper">
                            {formData.quantity > 0 && formData.splitType && (
                              <>
                                <IonIcon
                                  icon={ticketOutline}
                                  className="mr-1"
                                />
                                {calculateSplitGroups(
                                  formData.quantity,
                                  formData.splitType
                                )}
                              </>
                            )}
                          </IonNote>
                        </IonItem>
                      </IonList>

                      {/* Restrictions Selection */}
                      <div className="mt-4">
                        <IonLabel className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2">
                          <IonIcon
                            icon={informationCircleOutline}
                            className="mr-1 align-middle"
                          />
                          Ticket Restrictions
                        </IonLabel>

                        <div className="space-y-2">
                          {RESTRICTION_OPTIONS.map((restriction) => (
                            <IonItem
                              key={restriction.id}
                              lines="none"
                              className="bg-gray-50 dark:bg-gray-900 rounded-lg p-2"
                            >
                              <IonCheckbox
                                checked={formData.restrictions.includes(
                                  restriction.id
                                )}
                                onIonChange={(e) => {
                                  const checked = e.detail.checked;
                                  setFormData({
                                    ...formData,
                                    restrictions: checked
                                      ? [
                                          ...formData.restrictions,
                                          restriction.id,
                                        ]
                                      : formData.restrictions.filter(
                                          (r) => r !== restriction.id
                                        ),
                                  });
                                }}
                                labelPlacement="end"
                              >
                                <IonLabel className="ml-2">
                                  <h3 className="text-sm font-medium">
                                    {restriction.label}
                                  </h3>
                                </IonLabel>
                              </IonCheckbox>
                            </IonItem>
                          ))}

                          {/* Custom Restriction Input */}
                          <IonItem
                            lines="none"
                            className="bg-gray-50 dark:bg-gray-900 rounded-lg"
                          >
                            <IonLabel
                              position="stacked"
                              className="text-sm font-medium mb-1"
                            >
                              Other Restriction
                            </IonLabel>
                            <div className="flex items-center gap-2">
                              <IonInput
                                value={formData.customRestriction}
                                onIonChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    customRestriction: e.detail.value!,
                                  })
                                }
                                placeholder="Enter custom restriction"
                                className="text-base flex-1"
                              />
                              <IonButton
                                size="small"
                                fill="outline"
                                disabled={!formData.customRestriction.trim()}
                                onClick={() => {
                                  if (formData.customRestriction.trim()) {
                                    setFormData({
                                      ...formData,
                                      restrictions: [
                                        ...formData.restrictions,
                                        formData.customRestriction.trim(),
                                      ],
                                      customRestriction: '',
                                    });
                                  }
                                }}
                              >
                                Add
                              </IonButton>
                            </div>
                          </IonItem>

                          {/* Display selected restrictions */}
                          {formData.restrictions.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {formData.restrictions.map((restriction) => {
                                const predefined = RESTRICTION_OPTIONS.find(
                                  (r) => r.id === restriction
                                );
                                return (
                                  <IonChip
                                    key={restriction}
                                    color="secondary"
                                    onClick={() => {
                                      setFormData({
                                        ...formData,
                                        restrictions:
                                          formData.restrictions.filter(
                                            (r) => r !== restriction
                                          ),
                                      });
                                    }}
                                  >
                                    <IonLabel>
                                      {predefined
                                        ? predefined.label
                                        : restriction}
                                    </IonLabel>
                                    <IonIcon icon={closeCircleOutline} />
                                  </IonChip>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </IonCardContent>
                  </IonCard>
                </IonCol>

                {/* Right Column - Pricing & Quantity */}
                <IonCol size="12" sizeMd="6" className="pl-0 md:pl-2">
                  {/* Pricing Details */}
                  <IonCard className="mb-4 shadow-md">
                    <IonCardHeader className="pb-2 bg-gray-50 dark:bg-gray-900/50">
                      <IonCardTitle className="text-lg flex items-center">
                        <IonIcon icon={pricetagOutline} className="mr-2" />
                        Pricing & Quantity
                      </IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent>
                      <IonList className="space-y-3">
                        {/* Currency Selection */}
                        <IonItem
                          lines="none"
                          className="bg-gray-50 dark:bg-gray-900 rounded-lg"
                        >
                          <IonIcon
                            icon={walletOutline}
                            slot="start"
                            color="medium"
                          />
                          <IonLabel
                            position="stacked"
                            className="text-sm font-medium mb-1"
                          >
                            Currency
                          </IonLabel>
                          <IonSelect
                            value={formData.currency}
                            onIonChange={(e) =>
                              setFormData({
                                ...formData,
                                currency: e.detail.value!,
                              })
                            }
                            interface="popover"
                            className="text-base"
                          >
                            {CURRENCIES.map((currency) => (
                              <IonSelectOption
                                key={currency.code}
                                value={currency.code}
                              >
                                {currency.symbol} - {currency.name}
                              </IonSelectOption>
                            ))}
                          </IonSelect>
                        </IonItem>

                        <IonItem
                          lines="none"
                          className={`bg-gray-50 dark:bg-gray-900 rounded-lg ${
                            errors.purchasePrice ? 'ion-invalid' : ''
                          }`}
                        >
                          <IonIcon
                            icon={cashOutline}
                            slot="start"
                            color="medium"
                          />
                          <IonLabel
                            position="stacked"
                            className="text-sm font-medium mb-1"
                          >
                            {t('inventory.purchasePrice')}{' '}
                            <span className="text-red-500">*</span>
                          </IonLabel>
                          <IonInput
                            type="number"
                            value={formData.purchasePrice}
                            onIonInput={(e: any) => {
                              const value = e.target.value;
                              // Allow only numbers and decimal point
                              const numericValue = value.replace(
                                /[^0-9.]/g,
                                ''
                              );
                              // Ensure only one decimal point
                              const parts = numericValue.split('.');
                              const formattedValue =
                                parts.length > 2
                                  ? parts[0] + '.' + parts.slice(1).join('')
                                  : numericValue;
                              // Update the input value
                              e.target.value = formattedValue;
                              // Update form data
                              const price = parseFloat(formattedValue) || 0;
                              setFormData({
                                ...formData,
                                purchasePrice: price >= 0 ? price : 0,
                              });
                              // Clear error when user starts typing
                              if (errors.purchasePrice) {
                                setErrors({ ...errors, purchasePrice: '' });
                              }
                            }}
                            placeholder={`${getCurrencySymbol(
                              formData.currency
                            )}0.00`}
                            className="text-base font-semibold"
                            min="0"
                            step="0.01"
                            inputMode="decimal"
                            pattern="[0-9]*\.?[0-9]*"
                          />
                          <IonNote slot="helper">
                            Cost per ticket in {formData.currency}
                          </IonNote>
                          {errors.purchasePrice && (
                            <IonNote
                              slot="error"
                              className="text-red-500 text-xs mt-1"
                            >
                              {errors.purchasePrice}
                            </IonNote>
                          )}
                        </IonItem>
                        <IonItem
                          lines="none"
                          className={`bg-gray-50 dark:bg-gray-900 rounded-lg ${
                            errors.quantity ? 'ion-invalid' : ''
                          }`}
                        >
                          <IonIcon
                            icon={cubeOutline}
                            slot="start"
                            color="medium"
                          />
                          <IonLabel
                            position="stacked"
                            className="text-sm font-medium mb-1"
                          >
                            {t('inventory.quantity')}{' '}
                            <span className="text-red-500">*</span>
                          </IonLabel>
                          <IonInput
                            type="number"
                            value={
                              formData.quantity === 0 ? '' : formData.quantity
                            }
                            onIonInput={(e: any) => {
                              const value = e.target.value;
                              // Remove any non-numeric characters
                              const numericValue = value.replace(/[^0-9]/g, '');
                              // Update the input value
                              e.target.value = numericValue;
                              // Update form data - allow empty during typing
                              if (numericValue === '') {
                                setFormData({
                                  ...formData,
                                  quantity: 0, // Temporarily set to 0 to allow empty field
                                });
                              } else {
                                const quantity = parseInt(numericValue);
                                setFormData({
                                  ...formData,
                                  quantity: quantity > 0 ? quantity : 0,
                                });
                              }
                              // Clear error when user starts typing
                              if (errors.quantity) {
                                setErrors({ ...errors, quantity: '' });
                              }
                            }}
                            onIonBlur={(e: any) => {
                              // On blur, if empty or 0, set to 1
                              if (
                                !formData.quantity ||
                                formData.quantity === 0
                              ) {
                                setFormData({
                                  ...formData,
                                  quantity: 1,
                                });
                              }
                            }}
                            className="text-base font-semibold"
                            min="1"
                            max={saleItem?.quantity || undefined}
                            inputMode="numeric"
                            pattern="[0-9]*"
                          />
                          <IonNote slot="helper">
                            Number of tickets
                            {saleItem &&
                              saleItem.quantity &&
                              saleItem.quantity > 1 && (
                                <span className="text-orange-600 dark:text-orange-400">
                                  {' '}
                                  (Sale has {saleItem.quantity} tickets - you
                                  can add partial quantity)
                                </span>
                              )}
                          </IonNote>
                          {errors.quantity && (
                            <IonNote
                              slot="error"
                              className="text-red-500 text-xs mt-1"
                            >
                              {errors.quantity}
                            </IonNote>
                          )}
                        </IonItem>
                        <IonItem
                          lines="none"
                          className="bg-gray-50 dark:bg-gray-900 rounded-lg"
                        >
                          <IonIcon
                            icon={pricetagOutline}
                            slot="start"
                            color="medium"
                          />
                          <IonLabel
                            position="stacked"
                            className="text-sm font-medium mb-1"
                          >
                            Wanted Price
                          </IonLabel>
                          <IonInput
                            type="number"
                            value={formData.wantedPrice}
                            onIonChange={(e) =>
                              setFormData({
                                ...formData,
                                wantedPrice: parseFloat(e.detail.value!) || 0,
                              })
                            }
                            placeholder="Enter wanted sale price"
                            min="0"
                            step="0.01"
                            className="text-base"
                          />
                          <IonNote
                            slot="helper"
                            className="text-xs text-gray-500 dark:text-gray-400"
                          >
                            Optional: Price you want to sell for
                          </IonNote>
                        </IonItem>

                        {/* Total Cost Display */}
                        <div className="mt-4 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              Total Investment:
                            </span>
                            <span className="text-xl font-bold text-primary-600 dark:text-primary-400">
                              {getCurrencySymbol(formData.currency)}
                              {calculateTotalCost().toFixed(2)}
                            </span>
                          </div>
                          {formData.quantity > 1 && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {formData.quantity} tickets ×{' '}
                              {getCurrencySymbol(formData.currency)}
                              {formData.purchasePrice.toFixed(2)} each
                            </div>
                          )}
                        </div>
                      </IonList>
                    </IonCardContent>
                  </IonCard>

                  {/* Notes & Order Section */}
                  <IonCard className="shadow-md">
                    <IonCardHeader className="pb-2 bg-gray-50 dark:bg-gray-900/50">
                      <IonCardTitle className="text-lg flex items-center">
                        <IonIcon icon={informationCircleOutline} className="mr-2" />
                        Order Information
                      </IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent>
                      <IonList className="space-y-3">
                        <IonItem
                          lines="none"
                          className="bg-gray-50 dark:bg-gray-900 rounded-lg"
                        >
                          <IonLabel
                            position="stacked"
                            className="text-sm font-medium mb-1"
                          >
                            Order Number
                          </IonLabel>
                          <IonInput
                            value={formData.orderNumber}
                            onIonChange={(e) =>
                              setFormData({
                                ...formData,
                                orderNumber: e.detail.value!,
                              })
                            }
                            placeholder="e.g., ORD-12345, #ABC123..."
                            className="text-base"
                          />
                          <IonNote slot="helper" className="text-xs text-gray-500 dark:text-gray-400">
                            Optional: Order/booking reference from the club or supplier
                          </IonNote>
                        </IonItem>
                        
                        <IonItem
                          lines="none"
                          className="bg-gray-50 dark:bg-gray-900 rounded-lg"
                        >
                          <IonLabel
                            position="stacked"
                            className="text-sm font-medium mb-1"
                          >
                            Additional Notes
                          </IonLabel>
                          <IonInput
                            value={formData.notes}
                            onIonChange={(e) =>
                              setFormData({
                                ...formData,
                                notes: e.detail.value!,
                              })
                            }
                            placeholder="Add any additional notes or comments about this ticket..."
                            className="text-base"
                          />
                          <IonNote slot="helper" className="text-xs text-gray-500 dark:text-gray-400">
                            Optional: Any additional information about this purchase
                          </IonNote>
                        </IonItem>
                      </IonList>
                    </IonCardContent>
                  </IonCard>

                  {/* Stadium Information Card
                  {stadiumData && (
                    <IonCard className="shadow-md">
                      <IonCardHeader className="pb-2 bg-gray-50 dark:bg-gray-900/50">
                        <IonCardTitle className="text-lg flex items-center">
                          <IonIcon
                            icon={informationCircleOutline}
                            className="mr-2"
                          />
                          Stadium Information
                        </IonCardTitle>
                      </IonCardHeader>
                      <IonCardContent>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600 dark:text-gray-400">
                              Stadium:
                            </span>
                            <span className="font-medium">
                              {stadiumData.name}
                            </span>
                          </div>
                          {stadiumData.team && (
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600 dark:text-gray-400">
                                Team:
                              </span>
                              <span className="font-medium">
                                {stadiumData.team.name}
                              </span>
                            </div>
                          )}
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600 dark:text-gray-400">
                              Categories:
                            </span>
                            <span className="font-medium">
                              {categories.length}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600 dark:text-gray-400">
                              Total Blocks:
                            </span>
                            <span className="font-medium">{blocks.length}</span>
                          </div>
                        </div>
                      </IonCardContent>
                    </IonCard>
                  )} */}
                </IonCol>
              </IonRow>
            </IonGrid>
          </div>
        )}

        {/* Net Profit Display when coming from sale */}
        {saleItem && formData.purchasePrice > 0 && (
          <div className="sticky bottom-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 p-4 shadow-lg">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <IonIcon
                    icon={walletOutline}
                    className="text-lg text-green-600 dark:text-green-400"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Net Profit Calculation:
                  </span>
                </div>
                <div className="text-right">
                  {(() => {
                    // Calculate proportional sale revenue based on quantity being added
                    const proportionalRevenue =
                      (saleItem.salePrice || 0) * formData.quantity;
                    const totalCost =
                      formData.purchasePrice * formData.quantity;
                    const netProfit = proportionalRevenue - totalCost;

                    return (
                      <>
                        <div
                          className={`text-lg font-bold ${
                            netProfit >= 0
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-red-600 dark:text-red-400'
                          }`}
                        >
                          {getCurrencySymbol(formData.currency)}
                          {netProfit.toFixed(2)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Sale:{' '}
                          {getCurrencySymbol(
                            saleItem.currency || formData.currency
                          )}
                          {proportionalRevenue.toFixed(2)} - Cost:{' '}
                          {getCurrencySymbol(formData.currency)}
                          {totalCost.toFixed(2)}
                        </div>
                        {formData.quantity !== (saleItem.quantity || 1) && (
                          <div className="text-xs text-orange-600 dark:text-orange-400 mt-0.5">
                            Partial: {formData.quantity} of{' '}
                            {saleItem.quantity || 1} tickets
                          </div>
                        )}
                        {formData.quantity > 1 && (
                          <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                            (
                            {getCurrencySymbol(
                              saleItem.currency || formData.currency
                            )}
                            {saleItem.salePrice}/ticket -{' '}
                            {getCurrencySymbol(formData.currency)}
                            {formData.purchasePrice}/ticket)
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}
      </IonContent>

      <IonToast
        isOpen={showToast}
        onDidDismiss={() => setShowToast(false)}
        message={toastMessage}
        duration={3000}
        position="bottom"
      />
    </IonModal>
  );
};

export default AddTicketModal;
